import { api } from '@/lib/api/api'
import {
  type NotificationSettings,
  type RepeatSpec,
  ScheduledTaskSchema,
} from 'sprites-types/api/v2/scheduled-tasks'
import { z } from 'zod'

export type ScheduledTaskBase = z.infer<typeof ScheduledTaskSchema>

// The list endpoint enriches each task with the count of unseen results
export type ScheduledTask = ScheduledTaskBase & { unseenResultCount: number }
export type { NotificationSettings, RepeatSpec }

export interface ScheduledTaskPagination {
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

export interface ScheduledTasksResponse {
  success: boolean
  tasks: ScheduledTask[]
  pagination: ScheduledTaskPagination
}

export interface ScheduledTaskResponse {
  success: boolean
  task: ScheduledTask
}

export interface GetScheduledTasksParams {
  businessProfileId?: string
  status?: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'PAUSED'
  limit?: number
  offset?: number
}

/** Single tool call shape for create/update. */
export interface ScheduledTaskToolCallInput {
  function: {
    name: string
    arguments: string | Record<string, unknown>
  }
}

export interface CreateScheduledTaskInput {
  name: string
  description?: string | null
  toolCalls: ScheduledTaskToolCallInput[]
  timezone?: string | null
  repeatSpec?: RepeatSpec | null
  maxRuns?: number | null
  businessProfileId?: string
  firstRunAt?: string | null
  bookmark?: boolean
  notificationSettings?: NotificationSettings | null
}

export interface UpdateScheduledTaskInput {
  name?: string
  description?: string | null
  toolCalls?: ScheduledTaskToolCallInput[]
  timezone?: string | null
  repeatSpec?: RepeatSpec | null
  maxRuns?: number | null
  status?: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'PAUSED'
  firstRunAt?: string | null
  bookmark?: boolean
  notificationSettings?: NotificationSettings | null
}

export interface ScheduledTaskToolResult {
  id: string
  businessProfileId: string
  scheduledTaskId: string | null
  toolName: string
  toolArguments: Record<string, unknown>
  status: 'RUNNING' | 'COMPLETED' | 'FAILED'
  result: Record<string, unknown> | null
  error: string | null
  startedAt: string
  completedAt: string | null
  durationMs: number | null
  executionId: string
  seenAt: string | null
  createdAt: string
  updatedAt: string
}

export interface ScheduledTaskResultsResponse {
  success: boolean
  results: ScheduledTaskToolResult[]
  pagination: ScheduledTaskPagination
}

export interface GetScheduledTaskResultsParams {
  taskId: string
  status?: 'RUNNING' | 'COMPLETED' | 'FAILED'
  limit?: number
  offset?: number
}

export const scheduledTasksApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getScheduledTasks: builder.query<ScheduledTasksResponse, GetScheduledTasksParams>({
      query: (params: GetScheduledTasksParams = {}) => {
        const { businessProfileId, status, limit = 20, offset = 0 } = params
        const searchParams = new URLSearchParams({
          limit: limit.toString(),
          offset: offset.toString(),
        })

        if (businessProfileId) {
          searchParams.set('businessProfileId', businessProfileId)
        }

        if (status) {
          searchParams.set('status', status)
        }

        return {
          url: `v2/scheduled-tasks?${searchParams}`,
          credentials: 'include',
        }
      },
      transformResponse: (response: ScheduledTasksResponse): ScheduledTasksResponse => {
        return response
      },
      providesTags: ['ScheduledTasks'],
    }),
    getScheduledTask: builder.query<ScheduledTask, string>({
      query: (id) => ({
        url: `v2/scheduled-tasks/${id}`,
        credentials: 'include',
      }),
      transformResponse: (response: ScheduledTaskResponse): ScheduledTask => {
        return response.task
      },
      providesTags: (_result, _error, id) => [{ type: 'ScheduledTasks', id }],
    }),
    createScheduledTask: builder.mutation<ScheduledTaskResponse, CreateScheduledTaskInput>({
      query: (input) => ({
        url: 'v2/scheduled-tasks',
        method: 'POST',
        body: input,
        credentials: 'include',
      }),
      transformResponse: (response: ScheduledTaskResponse): ScheduledTaskResponse => {
        return response
      },
      invalidatesTags: ['ScheduledTasks'],
    }),
    updateScheduledTask: builder.mutation<
      ScheduledTaskResponse,
      { id: string; input: UpdateScheduledTaskInput }
    >({
      query: ({ id, input }) => ({
        url: `v2/scheduled-tasks/${id}`,
        method: 'PATCH',
        body: input,
        credentials: 'include',
      }),
      transformResponse: (response: ScheduledTaskResponse): ScheduledTaskResponse => {
        return response
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ScheduledTasks', id },
        'ScheduledTasks',
      ],
    }),
    skipNextScheduledTask: builder.mutation<ScheduledTaskResponse, string>({
      query: (id) => ({
        url: `v2/scheduled-tasks/${id}/skip-next`,
        method: 'POST',
        credentials: 'include',
      }),
      transformResponse: (response: ScheduledTaskResponse): ScheduledTaskResponse => {
        return response
      },
      invalidatesTags: (_result, _error, id) => [{ type: 'ScheduledTasks', id }, 'ScheduledTasks'],
    }),
    deleteScheduledTask: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `v2/scheduled-tasks/${id}`,
        method: 'DELETE',
        credentials: 'include',
      }),
      invalidatesTags: ['ScheduledTasks'],
    }),
    runNowScheduledTask: builder.mutation<
      { success: boolean; message: string; jobId: string },
      string
    >({
      query: (id) => ({
        url: `v2/scheduled-tasks/${id}/run-now`,
        method: 'POST',
        credentials: 'include',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'ScheduledTasks', id },
        'ScheduledTasks',
        { type: 'ScheduledTaskResults', id },
      ],
    }),
    getScheduledTaskResults: builder.query<
      ScheduledTaskResultsResponse,
      GetScheduledTaskResultsParams
    >({
      query: ({ taskId, status, limit = 20, offset = 0 }) => {
        const searchParams = new URLSearchParams({
          limit: limit.toString(),
          offset: offset.toString(),
        })

        if (status) {
          searchParams.set('status', status)
        }

        return {
          url: `v2/scheduled-tasks/${taskId}/results?${searchParams}`,
          credentials: 'include',
        }
      },
      providesTags: (_result, _error, { taskId }) => [{ type: 'ScheduledTaskResults', id: taskId }],
    }),
    markResultsSeen: builder.mutation<
      { success: boolean; markedCount: number },
      { taskId: string; resultIds: string[] }
    >({
      query: ({ taskId, resultIds }) => ({
        url: `v2/scheduled-tasks/${taskId}/results/seen`,
        method: 'POST',
        body: { resultIds },
        credentials: 'include',
      }),
      // Optimistically update the cache so the UI reflects "seen" immediately
      onQueryStarted: async ({ taskId, resultIds }, { dispatch, queryFulfilled }) => {
        const now = new Date().toISOString()
        // Patch all matching cache entries for this task's results
        const patchResult = dispatch(
          scheduledTasksApi.util.updateQueryData('getScheduledTaskResults', { taskId }, (draft) => {
            const idsSet = new Set(resultIds)
            for (const result of draft.results) {
              if (idsSet.has(result.id) && !result.seenAt && result.status !== 'RUNNING') {
                result.seenAt = now
              }
            }
          })
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      },
      invalidatesTags: (_result, _error, { taskId }) => [
        { type: 'ScheduledTaskResults', id: taskId },
        'ScheduledTasks', // Refresh task list to update unseenResultCount
      ],
    }),
  }),
})

export const {
  useGetScheduledTasksQuery,
  useGetScheduledTaskQuery,
  useCreateScheduledTaskMutation,
  useUpdateScheduledTaskMutation,
  useSkipNextScheduledTaskMutation,
  useDeleteScheduledTaskMutation,
  useRunNowScheduledTaskMutation,
  useGetScheduledTaskResultsQuery,
  useMarkResultsSeenMutation,
} = scheduledTasksApi
